<template>
  <component :is="isComponent ? 'div' : 'UPage'">
    <ConfigHeader
      v-if="!isComponent"
      :back-to="`/customers/${customerNo}/tenants`"
      :last-editor="config.lastEditor"
      :last-update="config.lastUpdate"
      :saving="saving"
      :tenant-name="tenantName"
      title="ELO Konfig"
      @save="saveConfig"
    />

    <component :is="isComponent ? 'div' : 'UPageBody'" :class="{ 'p-0': isComponent }">
      <div v-if="loading" class="flex justify-center py-6">
        <UIcon class="w-8 h-8 animate-spin text-muted" name="i-lucide-loader-circle" />
      </div>

      <div v-else class="space-y-6">
        <!-- Status & Activation -->
        <ConfigStatusCard
          :model-value="config.active"
          :disabled="!canWrite"
          title="ELO Automatisierung"
          description="Aktivieren oder deaktivieren Sie die ELO-Anbindung für diesen Mandanten."
          icon="i-lucide-archive"
          @update:model-value="onActiveToggle"
        />

        <USeparator class="my-8" />

        <ConfigChangelogAlert
          description="Bitte stellen Sie sicher, dass Ihre ELO-Umgebung die aktuellen Systemanforderungen erfüllt."
          release-type="connectelo"
        />

        <ConfigDocsAlert
          title="Einrichtung der ELO Komponente"
          description="Folgen Sie bitte den Anweisungen in unserer Konfigurations-Dokumentation, um die ELO-Komponente erfolgreich einzurichten."
          doc-key="system-config.elo.configuration"
          tenant-doc-key="tenant-config.elo-portal-config"
        />

        <!-- Akten-Konfigurationen -->
        <div class="grid grid-cols-1 gap-6">
          <AkteCard
            v-model:active="config.addressActive"
            v-model:name="config.addressName"
            v-model:path="config.addressPath"
            v-model:subfolders="config.addressSubfolders"
            :disabled="!canWrite"
            title="Adressakte"
          />
          <AkteCard
            v-model:active="config.projectActive"
            v-model:name="config.projectName"
            v-model:path="config.projectPath"
            v-model:subfolders="config.projectSubfolders"
            :disabled="!canWrite"
            title="Projektakte"
          />
          <AkteCard
            v-model:active="config.articleActive"
            v-model:name="config.articleName"
            v-model:path="config.articlePath"
            v-model:subfolders="config.articleSubfolders"
            :disabled="!canWrite"
            title="Artikelakte"
          />
        </div>

        <!-- Belegakte (Spezialfall wegen Mapping) -->
        <CollapsibleCard collapsible>
          <template #header>
            <div class="flex items-center gap-3">
              <h3 class="text-base font-semibold">Belegakte</h3>
              <USwitch v-model="config.receiptActive" :disabled="!canWrite" @click.stop />
              <UBadge :color="config.receiptActive ? 'success' : 'neutral'" variant="subtle">
                {{ config.receiptActive ? 'Aktiv' : 'Inaktiv' }}
              </UBadge>
            </div>
          </template>

          <div class="space-y-4">
            <p class="text-sm text-muted">
              Ordnerpfad mit „¶“ trennen. Unten kannst du Belegarten-Mappings manuell hinzufügen
              oder löschen.
            </p>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <UFormField label="Ablagepfad der Belegakte">
                <UInput v-model="config.receiptPath" :disabled="!canWrite" class="w-full" placeholder="¶Archiv¶Belege">
                  <template #trailing>
                    <UButton
                      v-if="!canWrite"
                      color="neutral"
                      label="¶"
                      size="xs"
                      variant="ghost"
                      @click="config.receiptPath += '¶'"
                    />
                  </template>
                </UInput>
              </UFormField>
              <UFormField label="Name der Belegakte">
                <UInput v-model="config.receiptName" :disabled="!canWrite" class="w-full" placeholder="Belegakte" />
              </UFormField>
            </div>

            <div class="mt-6 overflow-x-auto">
              <h4 class="text-sm font-bold mb-2">Mapping von Belegarten auf Freigabeworkflows</h4>
              <UTable
                class="min-w-[500px] sm:min-w-0"
                :columns="[
                  { accessorKey: 'invType', header: 'Belegart', id: 'invType' },
                  { accessorKey: 'flow', header: 'Workflow', id: 'flow' },
                  { id: 'actions', header: '' },
                ]"
                :data="config.invType2Flow"
              >
                <template #invType-cell="{ row }">
                  <UInput v-model="row.original.invType" :disabled="!canWrite" placeholder="Eingangsrechnung" size="sm" />
                </template>
                <template #flow-cell="{ row }">
                  <UInput v-model="row.original.flow" :disabled="!canWrite" placeholder="FI_Standard" size="sm" />
                </template>
                <template #actions-cell="{ row }">
                  <UButton
                    v-if="canWrite"
                    color="danger"
                    icon="i-heroicons-trash"
                    size="xs"
                    variant="ghost"
                    @click="removeInvType2Flow(row.original)"
                  />
                </template>
              </UTable>
              <UButton
                v-if="canWrite"
                class="mt-2"
                color="primary"
                icon="i-lucide-plus"
                label="Zeile hinzufügen"
                size="sm"
                variant="outline"
                @click="config.invType2Flow.push({ invType: '', flow: '' })"
              />
            </div>
          </div>
        </CollapsibleCard>

        <!-- LSN Management -->
        <CollapsibleCard
          description="Verwaltung der Last Sequence Numbers (LSN) für die Synchronisation."
          title="LSN Management"
        >
          <div v-if="loadingLsn" class="flex justify-center py-4">
            <UIcon class="w-6 h-6 animate-spin text-gray-400" name="i-lucide-loader-circle" />
          </div>
          <div v-else class="space-y-4 overflow-x-auto">
            <UTable
              class="min-w-[600px] sm:min-w-0"
              :columns="[
                { accessorKey: 'tableType', header: 'Typ', id: 'tableType' },
                { accessorKey: 'lastLsn', header: 'Letzte LSN', id: 'lastLsn' },
                { accessorKey: 'lastRun', header: 'Letzter Durchlauf', id: 'lastRun' },
                { accessorKey: 'lastChangedRun', header: 'Letzte Änderung', id: 'lastChangedRun' },
                { id: 'actions', header: '' },
              ]"
              :data="lsnState.tables"
            >
              <template #lastRun-cell="{ row }">
                {{ formatDate(row.original.lastRun) }}
              </template>
              <template #lastChangedRun-cell="{ row }">
                {{ formatDate(row.original.lastChangedRun) }}
              </template>
              <template #actions-cell="{ row }">
                <UButton
                  v-if="canWrite"
                  color="danger"
                  label="Reset"
                  size="xs"
                  variant="ghost"
                  @click="confirmResetLsn(row.original.tableType)"
                />
              </template>
            </UTable>
            <div v-if="canWrite" class="mt-4">
              <UButton
                color="danger"
                icon="i-lucide-trash"
                label="Alle LSN zurücksetzen"
                size="sm"
                variant="outline"
                @click="confirmResetLsn('ALL')"
              />
            </div>
          </div>
        </CollapsibleCard>
      </div>
    </component>

    <!-- Activation/Deactivation Confirmation Modal -->
    <ConfirmationModal
      v-model:open="isConfirmModalOpen"
      :description="
        config.active
          ? 'Bestätigen Sie die Deaktivierung der ELO-Komponente.'
          : 'Bestätigen Sie die Aktivierung der ELO-Komponente.'
      "
      :message="`Möchten Sie die ELO-Komponente wirklich ${config.active ? 'deaktivieren' : 'aktivieren'}?`"
      :title="config.active ? 'ELO deaktivieren' : 'ELO aktivieren'"
      @confirm="confirmToggle"
    />

    <ConfirmationModal
      v-model:open="isLsnResetConfirmModalOpen"
      :description="
        selectedLsnType === 'ALL'
          ? 'Alle LSNs werden auf 0 zurückgesetzt. Dies führt zu einer vollständigen Neusynchronisation.'
          : `Die LSN für ${selectedLsnType} wird auf 0 zurückgesetzt.`
      "
      message="Möchten Sie den Reset wirklich durchführen?"
      title="LSN zurücksetzen"
      @confirm="resetLsn"
    />
  </component>
</template>

<script lang="ts" setup>
import { onMounted, ref, watch } from 'vue'
import axios from 'axios'
import CollapsibleCard from '@/components/CollapsibleCard.vue'
import AkteCard from '@/components/AkteCard.vue'
import ConfirmationModal from '@/components/ConfirmationModal.vue'

interface InvType2Flow {
  invType: string
  flow: string
}

interface EloConfig {
  active: boolean
  addressActive: boolean
  addressPath: string
  addressName: string
  addressSubfolders: string[]
  projectActive: boolean
  projectPath: string
  projectName: string
  projectSubfolders: string[]
  articleActive: boolean
  articlePath: string
  articleName: string
  articleSubfolders: string[]
  receiptActive: boolean
  receiptPath: string
  receiptName: string
  invType2Flow: InvType2Flow[]
  lastUpdate?: string
  lastEditor?: string
}

interface LsnTable {
  tableType: string
  lastLsn: number
  lastRun?: string
  lastChangedRun?: string
}

interface LsnState {
  tables: LsnTable[]
}

defineProps({
  customerNo: {
    type: String,
    required: true,
  },
  tenantName: {
    type: String,
    required: true,
  },
  isComponent: {
    type: Boolean,
    default: false,
  },
  canWrite: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['dirty'])

const toast = useToast()

const loading = ref(true)
const saving = ref(false)
const isConfirmModalOpen = ref(false)
const isLsnResetConfirmModalOpen = ref(false)
const selectedLsnType = ref('')
const loadingLsn = ref(false)

defineExpose({
  saveConfig: () => saveConfig(),
})

const config = ref<EloConfig>({
  active: false,
  addressActive: false,
  addressPath: '',
  addressName: '',
  addressSubfolders: [],
  projectActive: false,
  projectPath: '',
  projectName: '',
  projectSubfolders: [],
  articleActive: false,
  articlePath: '',
  articleName: '',
  articleSubfolders: [],
  receiptActive: false,
  receiptPath: '',
  receiptName: '',
  invType2Flow: [],
})

const lsnState = ref<LsnState>({
  tables: [],
})

const onActiveToggle = () => {
  isConfirmModalOpen.value = true
}

const confirmToggle = () => {
  config.value.active = !config.value.active
  isConfirmModalOpen.value = false
}

const fetchConfig = async () => {
  loading.value = true
  try {
    const response = await axios.get(`/api/registry/v1/elo/config`)
    const data = response.data
    config.value = {
      ...data,
      invType2Flow: data.invType2Flow || [],
    }
    await fetchLsnState()
  } catch (error) {
    console.error('Failed to fetch ELO config', error)
    toast.add({
      title: 'Fehler',
      description: 'Fehler beim Laden der Konfiguration.',
      color: 'danger',
    })
  } finally {
    loading.value = false
  }
}

const formatDate = (dateString?: string) => {
  if (!dateString) return 'Nie'
  return new Date(dateString).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const saveConfig = async () => {
  saving.value = true
  try {
    await axios.put(`/api/registry/v1/elo/config`, config.value)
    toast.add({ title: 'Erfolg', description: 'Konfiguration gespeichert.', color: 'success' })
    await fetchConfig()
  } catch (error) {
    console.error('Failed to save ELO config', error)
    toast.add({
      title: 'Fehler',
      description: 'Fehler beim Speichern der Konfiguration.',
      color: 'danger',
    })
  } finally {
    saving.value = false
  }
}

const removeInvType2Flow = (item: InvType2Flow) => {
  const index = config.value.invType2Flow.indexOf(item)
  if (index > -1) {
    config.value.invType2Flow.splice(index, 1)
  }
}

const fetchLsnState = async () => {
  loadingLsn.value = true
  try {
    const response = await axios.get(`/api/registry/v1/elo/config/lsn`)
    lsnState.value = response.data
  } catch (error) {
    console.error('Failed to fetch LSN state', error)
  } finally {
    loadingLsn.value = false
  }
}

const confirmResetLsn = (type: string) => {
  selectedLsnType.value = type
  isLsnResetConfirmModalOpen.value = true
}

const resetLsn = async () => {
  isLsnResetConfirmModalOpen.value = false
  try {
    const params = new URLSearchParams()
    params.append('tableType', selectedLsnType.value)
    await axios.post(`/api/registry/v1/elo/config/lsn/reset`, params)
    toast.add({ title: 'Erfolg', description: 'LSN wurde zurückgesetzt.', color: 'success' })
    await fetchLsnState()
  } catch (error) {
    console.error('Failed to reset LSN', error)
    toast.add({
      title: 'Fehler',
      description: 'Fehler beim Zurücksetzen der LSN.',
      color: 'danger',
    })
  }
}

watch(
  config,
  () => {
    emit('dirty')
  },
  { deep: true },
)

onMounted(() => {
  fetchConfig()
})
</script>
